import React, { useState } from "react";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";
import { normalizeExternalUrl } from "./urlUtils";

export const DIRECTORY_SERVICE_TYPES = [
  "Food",
  "Health",
  "Training",
  "Youth",
  "Family",
  "Advice",
  "Community",
  "Faith",
  "Sports",
  "Other",
];

export const EMPTY_DIRECTORY_SUBMISSION = {
  name: "",
  type: "Community",
  description: "",
  website: "",
  contactEmail: "",
  contactPhone: "",
  address: "",
  postcode: "",
  confirmedAccurate: false,
};

const clean = (value) => String(value || "").trim();

export function validateDirectorySubmission(values) {
  const errors = {};
  const name = clean(values.name);
  const description = clean(values.description);
  const website = clean(values.website);
  const email = clean(values.contactEmail);
  const phone = clean(values.contactPhone);
  const address = clean(values.address);

  if (name.length < 2 || name.length > 120) {
    errors.name = "Enter a service or organisation name between 2 and 120 characters.";
  }
  if (!DIRECTORY_SERVICE_TYPES.includes(values.type)) {
    errors.type = "Choose a valid directory category.";
  }
  if (description.length < 20 || description.length > 1000) {
    errors.description = "Enter a description between 20 and 1,000 characters.";
  }
  if (website && !normalizeExternalUrl(website)) {
    errors.website = "Enter a valid website address.";
  }
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.contactEmail = "Enter a valid email address.";
  }
  if (!website && !email && !phone && !address) {
    errors.contact = "Add at least one public contact route: website, email, phone or address.";
  }
  if (!values.confirmedAccurate) {
    errors.confirmedAccurate = "Confirm that the listing information is accurate and may be published.";
  }

  return errors;
}

export function buildDirectorySubmissionPayload(values, createdAt) {
  return {
    name: clean(values.name),
    type: values.type,
    description: clean(values.description),
    website: normalizeExternalUrl(values.website),
    contactEmail: clean(values.contactEmail),
    contactPhone: clean(values.contactPhone),
    address: clean(values.address),
    postcode: clean(values.postcode).toUpperCase(),
    confirmedAccurate: values.confirmedAccurate === true,
    status: "pending",
    createdAt,
  };
}

export async function submitDirectoryListing(values) {
  const errors = validateDirectorySubmission(values);
  if (Object.keys(errors).length > 0) {
    const error = new Error("DIRECTORY_SUBMISSION_INVALID");
    error.validationErrors = errors;
    throw error;
  }

  return addDoc(
    collection(db, "directorySubmissions"),
    buildDirectorySubmissionPayload(values, serverTimestamp())
  );
}

function FieldError({ id, message }) {
  return message ? <span id={id} className="text-xs text-amber-200">{message}</span> : null;
}

export default function DirectorySubmissionForm() {
  const [values, setValues] = useState({ ...EMPTY_DIRECTORY_SUBMISSION });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [success, setSuccess] = useState("");

  const update = (field, value) => {
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => {
      if (!current[field] && !(field === "website" || field === "contactEmail" || field === "contactPhone" || field === "address")) return current;
      const next = { ...current };
      delete next[field];
      if (["website", "contactEmail", "contactPhone", "address"].includes(field)) delete next.contact;
      return next;
    });
    setSuccess("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = validateDirectorySubmission(values);
    setErrors(nextErrors);
    setSubmitError("");
    setSuccess("");
    if (Object.keys(nextErrors).length > 0) return;

    setSubmitting(true);
    try {
      await submitDirectoryListing(values);
      setValues({ ...EMPTY_DIRECTORY_SUBMISSION });
      setErrors({});
      setSuccess("Thank you. Your listing has been sent for review and will appear after approval.");
    } catch (error) {
      console.error("Unable to submit directory listing", error);
      setSubmitError("Your listing could not be submitted. Check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const describedBy = (field, extra = "") => [errors[field] ? `${field}-error` : "", extra].filter(Boolean).join(" ") || undefined;

  return (
    <section id="add-directory-listing" className="scroll-mt-28 rounded-2xl bg-white/5 border border-white/10 p-4">
      <h2 className="font-semibold text-lg">Add a directory listing</h2>
      <p className="text-white/70 text-sm mt-1">
        Submit a local service, group or organisation. UUN reviews every submission before it becomes public.
      </p>

      {Object.keys(errors).length > 0 && (
        <div role="alert" className="mt-3 rounded-xl border border-amber-300/30 bg-amber-300/10 p-3 text-sm text-amber-100">
          Please check the highlighted listing information.
        </div>
      )}
      {submitError && <div role="alert" className="mt-3 rounded-xl border border-amber-300/30 bg-amber-300/10 p-3 text-sm text-amber-100">{submitError}</div>}
      {success && <div role="status" className="mt-3 rounded-xl border border-emerald-300/30 bg-emerald-300/10 p-3 text-sm text-emerald-100">{success}</div>}

      <form onSubmit={handleSubmit} noValidate className="grid gap-3 mt-4 md:grid-cols-2">
        <label className="grid gap-1 text-sm" htmlFor="directory-name">
          Service or organisation name <span aria-hidden="true">*</span>
          <input
            id="directory-name"
            value={values.name}
            onChange={(event) => update("name", event.target.value)}
            maxLength={120}
            required
            aria-invalid={Boolean(errors.name)}
            aria-describedby={describedBy("name")}
            className="bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-sm"
          />
          <FieldError id="name-error" message={errors.name} />
        </label>

        <label className="grid gap-1 text-sm" htmlFor="directory-type">
          Category <span aria-hidden="true">*</span>
          <select
            id="directory-type"
            value={values.type}
            onChange={(event) => update("type", event.target.value)}
            aria-invalid={Boolean(errors.type)}
            aria-describedby={describedBy("type")}
            className="bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-sm"
          >
            {DIRECTORY_SERVICE_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
          </select>
          <FieldError id="type-error" message={errors.type} />
        </label>

        <label className="grid gap-1 text-sm md:col-span-2" htmlFor="directory-description">
          Description <span aria-hidden="true">*</span>
          <textarea
            id="directory-description"
            value={values.description}
            onChange={(event) => update("description", event.target.value)}
            minLength={20}
            maxLength={1000}
            required
            aria-invalid={Boolean(errors.description)}
            aria-describedby={describedBy("description", "directory-description-help")}
            className="bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-sm min-h-[110px]"
          />
          <span id="directory-description-help" className="text-xs text-white/60">Explain what is offered, who it is for and any important eligibility information.</span>
          <FieldError id="description-error" message={errors.description} />
        </label>

        <label className="grid gap-1 text-sm" htmlFor="directory-website">
          Website
          <input
            id="directory-website"
            type="url"
            value={values.website}
            onChange={(event) => update("website", event.target.value)}
            maxLength={300}
            aria-invalid={Boolean(errors.website || errors.contact)}
            aria-describedby={describedBy("website", errors.contact ? "directory-contact-error" : "")}
            placeholder="https://example.org"
            className="bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-sm"
          />
          <FieldError id="website-error" message={errors.website} />
        </label>

        <label className="grid gap-1 text-sm" htmlFor="directory-email">
          Public email
          <input
            id="directory-email"
            type="email"
            value={values.contactEmail}
            onChange={(event) => update("contactEmail", event.target.value)}
            maxLength={254}
            aria-invalid={Boolean(errors.contactEmail || errors.contact)}
            aria-describedby={describedBy("contactEmail", errors.contact ? "directory-contact-error" : "")}
            className="bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-sm"
          />
          <FieldError id="contactEmail-error" message={errors.contactEmail} />
        </label>

        <label className="grid gap-1 text-sm" htmlFor="directory-phone">
          Public phone
          <input
            id="directory-phone"
            type="tel"
            value={values.contactPhone}
            onChange={(event) => update("contactPhone", event.target.value)}
            maxLength={40}
            aria-invalid={Boolean(errors.contact)}
            aria-describedby={errors.contact ? "directory-contact-error" : undefined}
            className="bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-sm"
          />
        </label>

        <label className="grid gap-1 text-sm" htmlFor="directory-address">
          Public address
          <input
            id="directory-address"
            value={values.address}
            onChange={(event) => update("address", event.target.value)}
            maxLength={300}
            aria-invalid={Boolean(errors.contact)}
            aria-describedby={errors.contact ? "directory-contact-error" : undefined}
            className="bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-sm"
          />
        </label>

        <label className="grid gap-1 text-sm" htmlFor="directory-postcode">
          Postcode
          <input
            id="directory-postcode"
            value={values.postcode}
            onChange={(event) => update("postcode", event.target.value)}
            maxLength={12}
            autoComplete="postal-code"
            className="bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-sm"
          />
        </label>

        <div className="md:col-span-2">
          <FieldError id="directory-contact-error" message={errors.contact} />
        </div>

        <label className="md:col-span-2 flex items-start gap-2 rounded-xl border border-white/10 bg-black/20 p-3 text-sm" htmlFor="directory-confirmed">
          <input
            id="directory-confirmed"
            type="checkbox"
            checked={values.confirmedAccurate}
            onChange={(event) => update("confirmedAccurate", event.target.checked)}
            aria-invalid={Boolean(errors.confirmedAccurate)}
            aria-describedby={errors.confirmedAccurate ? "confirmedAccurate-error" : "directory-public-note"}
          />
          <span>
            I confirm that this information is accurate and that I have permission to submit it for publication.
            <span id="directory-public-note" className="block mt-1 text-xs text-white/60">Only enter contact details that may be displayed publicly.</span>
            <FieldError id="confirmedAccurate-error" message={errors.confirmedAccurate} />
          </span>
        </label>

        <button
          type="submit"
          disabled={submitting}
          className="md:col-span-2 px-4 py-3 rounded-xl bg-[#E63946] hover:opacity-90 disabled:opacity-60 transition font-semibold"
        >
          {submitting ? "Submitting…" : "Submit listing for approval"}
        </button>
      </form>
    </section>
  );
}
